import React, { FC, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import type { ClaimResult, ClaimSubscription } from '@apiRoutes'
import { Loading } from '@components'
import { ClaimDoneState } from './perform-claim'
import {
  ClaimLayout,
  claimPath,
  fillCopy,
  fillText,
  formatDate,
  model
} from './shared'
import styles from './index.module.scss'

// Same listings in every environment: there are no dev/test builds in the stores
export const APP_STORE_URL = 'https://apps.apple.com/us/app/id6447322997'
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.akiliinteractive.t01a'
// The page lists the current Instructions for Use, so the link stays right when the IFU is revised
export const IFU_URL = 'https://www.endeavorotc.com/IFU/'

const DownloadIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const subscriptionCopy = model.done.subscription

const cancelWhere = (store: string): string => {
  if (store === 'app_store' || store === 'mac_app_store') {
    return subscriptionCopy.where.app_store
  }
  if (store === 'play_store') {
    return subscriptionCopy.where.play_store
  }
  return subscriptionCopy.where.other
}

// The patient already pays for EndeavorOTC, so the practice's two months start after their subscription ends
const SubscriptionNotice: FC<{
  subscription: ClaimSubscription
  endsAt: string
}> = ({ subscription, endsAt }) => {
  const paidThrough = formatDate(subscription.endsAt)

  return (
    <section
      className={styles.notice}
      data-testid="claim-done-subscription"
      aria-labelledby="claim-done-subscription-title"
    >
      <h3 id="claim-done-subscription-title">{subscriptionCopy.title}</h3>
      <p>{fillText(subscriptionCopy.extended, { paidThrough, endsAt })}</p>
      {subscription.willRenew ? (
        <>
          <p>
            <b>{fillText(subscriptionCopy.willRenew, { paidThrough })}</b>{' '}
            {fillText(subscriptionCopy.howToCancel, {
              endsAt,
              where: cancelWhere(subscription.store)
            })}
          </p>
          <p className={styles.muted}>{fillCopy(subscriptionCopy.help)}</p>
        </>
      ) : (
        <p>{subscriptionCopy.wontRenew}</p>
      )}
    </section>
  )
}

const isClaimResult = (value: unknown): value is ClaimResult => {
  const v = value as ClaimResult | null
  return !!v && typeof v.endsAt === 'string'
}

const ClaimDone: FC = () => {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const navState = (location.state || null) as Partial<ClaimDoneState> | null
  const claim = isClaimResult(navState?.claim) ? navState!.claim! : null
  const practiceName = navState?.practiceName || null

  // Page refresh (or opening this address directly) loses navigation state. Never
  // activate from here: the claim page re-checks a used link for this account and
  // asks before activating an unused one, then comes back with the result.
  useEffect(() => {
    if (!claim) {
      navigate(claimPath(token), { replace: true })
    }
  }, [claim, navigate, token])

  const renderBody = () => {
    if (!claim) {
      return (
        <div className={styles.loading} data-testid="claim-done-loading">
          <Loading />
        </div>
      )
    }

    const endsAt = formatDate(claim.endsAt)
    const email = claim.accountEmail

    return (
      <div className={styles.done} data-testid="claim-done">
        <div className={styles.badge_ok} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <header>
          <h2 className={styles.title}>{model.done.title}</h2>
          <p className={styles.body}>
            {model.done.activeUntil.split('{{endsAt}}')[0]}
            <b>{endsAt}</b>
            {model.done.activeUntil.split('{{endsAt}}')[1]}
          </p>
        </header>
        {claim.subscription && (
          <SubscriptionNotice
            subscription={claim.subscription}
            endsAt={endsAt}
          />
        )}
        <dl className={styles.kv}>
          <dt>{model.done.account}</dt>
          <dd>{email}</dd>
          <dt>{model.done.status}</dt>
          <dd>
            <span className={styles.status_pill}>{model.done.statusValue}</span>
          </dd>
          <dt>{model.done.ends}</dt>
          <dd>{fillText(model.done.endsValue, { endsAt })}</dd>
        </dl>
        <ol className={styles.todo}>
          <li>
            <span className={styles.todo_n}>1</span>
            <div>
              <b>{model.done.getApp}</b>
              <div className={styles.store_row}>
                <a
                  className={styles.store}
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <DownloadIcon />
                  {model.done.appStore}
                </a>
                <a
                  className={styles.store}
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <DownloadIcon />
                  {model.done.googlePlay}
                </a>
              </div>
            </div>
          </li>
          <li>
            <span className={styles.todo_n}>2</span>
            <div>
              <b>
                {email
                  ? fillText(model.done.signInWith, { email })
                  : model.done.signInWithFallback}
              </b>
              <span className={styles.muted}>{model.done.unlocks}</span>
            </div>
          </li>
          <li>
            <span className={styles.todo_n}>3</span>
            <div>
              <b>
                <a href={IFU_URL} target="_blank" rel="noreferrer">
                  {model.done.readIfu}
                </a>
              </b>
              <span className={styles.muted}>{model.done.readIfuBody}</span>
            </div>
          </li>
        </ol>
      </div>
    )
  }

  return <ClaimLayout practiceName={practiceName}>{renderBody()}</ClaimLayout>
}

export default ClaimDone
